/* eslint-disable eslint-comments/disable-enable-pair */

/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useAutoApprovedQualifications } from "@src/appV2/Licenses/useAutoApprovedQualifications";
import type { HiringFeedsResponse } from "@src/appV2/redesign/HiringFeed/api/hiringFeed.schema";
import {
  PLACEMENT_COHORT,
  type PlacementCandidate,
} from "@src/appV2/redesign/PlacementCandidate/types";
import { type DiscoveryPlacementsResponse } from "@src/appV2/redesign/Placements/api/useFetchDiscoveryPlacements";
import { LoadingSpinner, Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { type Worker } from "@src/appV2/Worker/api/types";
import { type UseInfiniteQueryResult } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { Fragment, useState } from "react";
import { InView } from "react-intersection-observer";

import { useCredentialRequirementConfiguration } from "../../Documents/useCredentialRequirementConfiguration";
import { transformResponseIntoHiringFeed } from "../../HiringFeed/api/usePaginatedHiringFeed";
import { useOnboardingRequirement } from "../../PlacementCandidate/hooks/useOnboardingRequirement";
import { augmentYearsOfExperienceWithWorkerLicenses } from "../../PlacementCandidate/utils";
import { transformDiscoveryPlacementToData } from "../api/transformDiscoveryPlacementToData";
import { useGetIncentives } from "../api/useGetIncentives";
import { type InterviewOnJobCardsConfig } from "../components/PlacementCard/types";
import { usePlacementCohorts } from "../hooks/usePlacementCohorts";
import { usePayTransparencyRates } from "../lib/usePayTransparencyRates";
import { type GetPlacementsForWorkplaceQuery } from "../types/fetchPlacements.schema";
import { useHiringIncentiveProgram } from "../useHiringIncentiveProgram";
import { useIsJobInterviewsEnabled } from "../useIsJobInterviewsEnabled";
import { hiringFeedToPlacement } from "../utils/placement-data";
import { CardInterviewBookingContainer } from "./CardInterviewBookingContainer";
import { FiltersTopBar } from "./FiltersTopBar";
import { PlacementCard } from "./PlacementCard";
import { PlacementsLoader } from "./PlacementsLoader";
import { ReferralBanner } from "./ReferralBanner/ReferralBanner";
import { SignOnBonusBanner } from "./SignOnBonusBanner";

interface OpenPlacementListViewProps {
  placementCandidate: PlacementCandidate;
  worker: Worker;
  filter: GetPlacementsForWorkplaceQuery["filter"];
  setFilter: (filter: GetPlacementsForWorkplaceQuery["filter"]) => void;
  showApplications: boolean;
  hiringFeedResults: UseInfiniteQueryResult<HiringFeedsResponse>;
  isDiscoveryMigrationEnabled: boolean;
  discoveryResults: UseInfiniteQueryResult<DiscoveryPlacementsResponse>;
}

interface InterviewBookingState {
  placementId: string;
  workplaceId: string;
  placementCandidateId: string;
  preSelectedInterviewDate?: string;
}

export function OpenPlacementListView(props: OpenPlacementListViewProps) {
  const {
    placementCandidate,
    worker,
    filter,
    setFilter,
    showApplications,
    hiringFeedResults,
    isDiscoveryMigrationEnabled,
    discoveryResults,
  } = props;
  const { hiringIncentiveProgram } = useHiringIncentiveProgram();

  const interviewBookingModalState = useModalState();
  const [interviewBookingState, setInterviewBookingState] = useState<
    InterviewBookingState | undefined
  >(undefined);
  const [bookedPlacementIds, setBookedPlacementIds] = useState<Map<string, string>>(new Map());

  const handleOpenInterviewBooking = (params: InterviewBookingState) => {
    setInterviewBookingState(params);
    interviewBookingModalState.openModal();
  };

  const yearsOfExperienceByQualification = augmentYearsOfExperienceWithWorkerLicenses(
    placementCandidate.yearsOfExperienceByQualification,
    worker.licenses
  );

  const {
    data: hiringFeedData,
    isSuccess: isHiringFeedSuccess,
    hasNextPage: hasNextPageHiringFeed,
    fetchNextPage: fetchNextPageHiringFeed,
    isFetchingNextPage: isFetchingNextPageHiringFeed,
    refetch: refetchHiringFeed,
  } = hiringFeedResults;

  const {
    data: discoveryData,
    isSuccess: isDiscoverySuccess,
    hasNextPage: hasNextPageDiscovery,
    fetchNextPage: fetchNextPageDiscovery,
    isFetchingNextPage: isFetchingNextPageDiscovery,
    refetch: refetchDiscovery,
  } = discoveryResults;

  // Unified accessors — select active data source based on feature flag
  const activeIsSuccess = isDiscoveryMigrationEnabled ? isDiscoverySuccess : isHiringFeedSuccess;
  const activeHasNextPage = isDiscoveryMigrationEnabled
    ? hasNextPageDiscovery
    : hasNextPageHiringFeed;
  const activeFetchNextPage = isDiscoveryMigrationEnabled
    ? fetchNextPageDiscovery
    : fetchNextPageHiringFeed;
  const activeIsFetchingNextPage = isDiscoveryMigrationEnabled
    ? isFetchingNextPageDiscovery
    : isFetchingNextPageHiringFeed;
  const activeRefetch = isDiscoveryMigrationEnabled ? refetchDiscovery : refetchHiringFeed;
  const activeData = isDiscoveryMigrationEnabled ? discoveryData : hiringFeedData;
  const activeHasResults = (activeData?.pages ?? []).some((page) => page.data.length > 0);

  const { data: signOnBonuses, isSuccess: isGetIncentivesSuccess } = useGetIncentives(
    {
      workerId: worker.userId,
      queryParams: {
        filter: { sourceType: "Placement" },
      },
    },
    { enabled: hiringIncentiveProgram.enabled }
  );

  const { getCachedPayRatesForPlacement, isLoading: isPayDataLoading } = usePayTransparencyRates();

  const isSignOnBonusPaid = signOnBonuses?.data.some(
    (incentive) => incentive.attributes.status === "paid"
  );
  const hiringFeeds = hiringFeedData?.pages.flatMap((page) => page.data);

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  // Get available states from API response meta (for state filter)
  const availableStates = isDiscoveryMigrationEnabled
    ? (discoveryData?.pages[0]?.meta?.availableStates ?? [])
    : (hiringFeedData?.pages[0]?.meta?.availableStates ?? []);
  const shouldShowStateFilter = availableStates.length > 1;

  const autoApprovedQualifications = useAutoApprovedQualifications();
  const { isCompleted: isProfileCompleted } = useOnboardingRequirement(worker.userId);
  const isJobInterviewsEnabled = useIsJobInterviewsEnabled();

  const interviewOnJobCardsConfig: InterviewOnJobCardsConfig = {
    isProfileCompleted,
    autoApprovedQualifications,
    isJobInterviewsEnabled,
  };

  const { cohorts, flexReviewsInJobs } = usePlacementCohorts();

  const { enabled: isFlexReviewsInJobsEnabled, minFlexRatingsCountToShow } = flexReviewsInJobs;
  const showWorkplaceRatings =
    isFlexReviewsInJobsEnabled && cohorts.includes(PLACEMENT_COHORT.FLEX_REVIEWS_IN_JOBS);

  return (
    <>
      {/* Sticky Filters Top Bar - rendered here to keep it local to the list and reliably sticky */}
      {!showApplications && activeIsSuccess && (
        <FiltersTopBar
          filter={filter}
          setFilter={setFilter}
          worker={worker}
          placementCandidateId={placementCandidate.id}
          availableStates={availableStates}
          shouldShowStateFilter={shouldShowStateFilter}
        />
      )}
      {!showApplications && activeIsSuccess && activeHasResults && <ReferralBanner />}

      {isEmpty(activeData) && <PlacementsLoader />}

      {activeIsSuccess && !activeHasResults && (
        <Stack justifyContent="center" flex={1} alignItems="center" sx={{ pt: 5 }}>
          <Text variant="body1">There are no relevant jobs for you yet! </Text>
        </Stack>
      )}

      {isDiscoveryMigrationEnabled && activeHasResults && isDefined(discoveryData) && (
        <>
          {discoveryData.pages.some((page) =>
            page.data.some(
              (placement) =>
                !placement.meta.matchedType ||
                placement.meta.matchedType === "exact" ||
                placement.meta.matchedType === "recentlyAdded"
            )
          ) ? (
            <Stack direction="column" spacing={6} pt={1}>
              {discoveryData.pages.flatMap((discoveryPage, pageIndex) => {
                let globalIndex = 0;
                for (let index = 0; index < pageIndex; index += 1) {
                  globalIndex += discoveryData.pages[index].data.length;
                }

                return discoveryPage.data.map((discoveryItem, placementIndex) => {
                  const currentGlobalIndex = globalIndex + placementIndex;
                  const placementData = transformDiscoveryPlacementToData(
                    discoveryItem,
                    discoveryPage.included,
                    credentialRequirementConfiguration
                  );

                  if (!isDefined(placementData)) {
                    return undefined;
                  }

                  const itemKey = discoveryItem.id;

                  const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
                    {
                      id: placementData.id,
                      workplaceId: placementData.workplace.id,
                      workerTypes: placementData.workerTypes,
                      jobShifts: placementData.jobShifts,
                      transparencyPayRates: placementData.transparencyPayRates,
                      attributes: { payRange: placementData.payRange },
                    },
                    yearsOfExperienceByQualification,
                    false,
                    placementCandidate.payAndScheduleFormData?.benefits?.combined
                  );

                  return !discoveryItem.meta.matchedType ||
                    discoveryItem.meta.matchedType === "exact" ||
                    discoveryItem.meta.matchedType === "recentlyAdded" ? (
                    <Fragment key={itemKey}>
                      {currentGlobalIndex === 2 &&
                        hiringIncentiveProgram.enabled &&
                        !isSignOnBonusPaid &&
                        !placementCandidate.isRestricted &&
                        isGetIncentivesSuccess && (
                          <SignOnBonusBanner
                            placementId={placementData.id}
                            placementCandidate={placementCandidate}
                          />
                        )}
                      <PlacementCard
                        key={itemKey}
                        placement={placementData}
                        placementCandidateId={placementCandidate.id}
                        isRestricted={placementCandidate.isRestricted ?? false}
                        interviewOnJobCardsConfig={interviewOnJobCardsConfig}
                        pageIndex={pageIndex}
                        placementIndex={placementIndex}
                        loadingPayData={isPayDataLoading}
                        minFlexRatingsCountToShow={minFlexRatingsCountToShow}
                        showWorkplaceRatings={showWorkplaceRatings}
                        minMaxPayEstimate={minMaxPayEstimate}
                        source="placement_card"
                        bookedInterviewDate={bookedPlacementIds.get(placementData.id)}
                        onOpenInterviewBooking={handleOpenInterviewBooking}
                      />
                    </Fragment>
                  ) : undefined;
                });
              })}
            </Stack>
          ) : undefined}
          {discoveryData.pages.some((page) =>
            page.data.some((placement) => placement.meta.matchedType === "discovery")
          ) ? (
            <Box>
              <Box>
                <Stack sx={{ textAlign: "center", marginY: 6, marginBottom: 8 }} spacing={1}>
                  {discoveryData.pages.every((page) =>
                    page.data.every((placement) => placement.meta.matchedType !== "exact")
                  ) ? (
                    <>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        No jobs match your filters.
                      </Text>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        Update your filters or discover other jobs below.
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        You&apos;ve reached the end of your filtered matches.
                      </Text>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        Update filters or explore more jobs below!
                      </Text>
                    </>
                  )}
                </Stack>
              </Box>
              <Stack
                direction="column"
                spacing={8}
                sx={{
                  background: (theme) => theme.palette.background.quaternary,
                  marginX: -5,
                  paddingX: 5,
                  paddingY: 8,
                }}
              >
                <Text variant="h4">Explore other jobs you might like</Text>
                <Stack direction="column" spacing={6}>
                  {discoveryData.pages.flatMap((discoveryPage, pageIndex) => {
                    return discoveryPage.data.map((discoveryItem, placementIndex) => {
                      const placementData = transformDiscoveryPlacementToData(
                        discoveryItem,
                        discoveryPage.included,
                        credentialRequirementConfiguration
                      );

                      if (!isDefined(placementData)) {
                        return undefined;
                      }

                      const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
                        {
                          id: placementData.id,
                          workplaceId: placementData.workplace.id,
                          workerTypes: placementData.workerTypes,
                          jobShifts: placementData.jobShifts,
                          transparencyPayRates: placementData.transparencyPayRates,
                          attributes: { payRange: placementData.payRange },
                        },
                        yearsOfExperienceByQualification,
                        false,
                        placementCandidate.payAndScheduleFormData?.benefits?.combined
                      );

                      return discoveryItem.meta.matchedType === "discovery" ? (
                        <Fragment key={discoveryItem.id}>
                          <PlacementCard
                            placement={placementData}
                            placementCandidateId={placementCandidate.id}
                            isRestricted={placementCandidate.isRestricted ?? false}
                            interviewOnJobCardsConfig={interviewOnJobCardsConfig}
                            pageIndex={pageIndex}
                            placementIndex={placementIndex}
                            loadingPayData={isPayDataLoading}
                            minFlexRatingsCountToShow={minFlexRatingsCountToShow}
                            showWorkplaceRatings={showWorkplaceRatings}
                            minMaxPayEstimate={minMaxPayEstimate}
                            source="placement_card"
                            bookedInterviewDate={bookedPlacementIds.get(placementData.id)}
                            onOpenInterviewBooking={handleOpenInterviewBooking}
                          />
                        </Fragment>
                      ) : undefined;
                    });
                  })}
                </Stack>
              </Stack>
            </Box>
          ) : undefined}
        </>
      )}

      {!isDiscoveryMigrationEnabled && activeHasResults && isDefined(hiringFeedData) && (
        <>
          {hiringFeeds?.some(
            (placement) => !placement.meta.matchedType || placement.meta.matchedType === "exact"
          ) ? (
            <Stack direction="column" spacing={6} pt={1}>
              {hiringFeedData?.pages.flatMap((hiringFeedPage, pageIndex) => {
                let globalIndex = 0;
                for (let index = 0; index < pageIndex; index += 1) {
                  globalIndex += hiringFeedData.pages[index].data.length;
                }

                return hiringFeedPage.data.map((hiringFeedItem, placementIndex) => {
                  const currentGlobalIndex = globalIndex + placementIndex;
                  const hiringFeedWithDetails = transformResponseIntoHiringFeed(
                    hiringFeedItem,
                    hiringFeedPage.included,
                    credentialRequirementConfiguration
                  );

                  const itemKey = hiringFeedItem.id;

                  const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
                    {
                      id: hiringFeedWithDetails.id,
                      workplaceId: hiringFeedWithDetails.workplace.id,
                      workerTypes: hiringFeedWithDetails.workerTypes,
                      jobShifts: hiringFeedWithDetails.jobShifts,
                      transparencyPayRates: hiringFeedWithDetails.transparencyPayRates,
                      attributes: { payRange: hiringFeedWithDetails.payRange },
                    },
                    yearsOfExperienceByQualification,
                    false,
                    placementCandidate.payAndScheduleFormData?.benefits?.combined
                  );

                  return !hiringFeedItem.meta.matchedType ||
                    hiringFeedItem.meta.matchedType === "exact" ||
                    hiringFeedItem.meta.matchedType === "recentlyAdded" ? (
                    <Fragment key={itemKey}>
                      {currentGlobalIndex === 2 &&
                        hiringIncentiveProgram.enabled &&
                        !isSignOnBonusPaid &&
                        !placementCandidate.isRestricted &&
                        isGetIncentivesSuccess && (
                          <SignOnBonusBanner
                            placementId={hiringFeedWithDetails.id}
                            placementCandidate={placementCandidate}
                          />
                        )}
                      <PlacementCard
                        key={itemKey}
                        placement={hiringFeedToPlacement(hiringFeedWithDetails)}
                        placementCandidateId={placementCandidate.id}
                        isRestricted={placementCandidate.isRestricted ?? false}
                        interviewOnJobCardsConfig={interviewOnJobCardsConfig}
                        pageIndex={pageIndex}
                        placementIndex={placementIndex}
                        loadingPayData={isPayDataLoading}
                        minFlexRatingsCountToShow={minFlexRatingsCountToShow}
                        showWorkplaceRatings={showWorkplaceRatings}
                        minMaxPayEstimate={minMaxPayEstimate}
                        source="placement_card"
                        bookedInterviewDate={bookedPlacementIds.get(hiringFeedWithDetails.id)}
                        onOpenInterviewBooking={handleOpenInterviewBooking}
                      />
                    </Fragment>
                  ) : undefined;
                });
              })}
            </Stack>
          ) : undefined}
          {hiringFeeds?.some((placement) => placement.meta.matchedType === "discovery") ? (
            <Box>
              <Box>
                <Stack sx={{ textAlign: "center", marginY: 6, marginBottom: 8 }} spacing={1}>
                  {hiringFeeds?.filter((placement) => placement.meta.matchedType === "exact")
                    .length === 0 ? (
                    <>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        No jobs match your filters.
                      </Text>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        Update your filters or discover other jobs below.
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        You&apos;ve reached the end of your filtered matches.
                      </Text>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        Update filters or explore more jobs below!
                      </Text>
                    </>
                  )}
                </Stack>
              </Box>
              <Stack
                direction="column"
                spacing={8}
                sx={{
                  background: (theme) => theme.palette.background.quaternary,
                  marginX: -5,
                  paddingX: 5,
                  paddingY: 8,
                }}
              >
                <Text variant="h4">Explore other jobs you might like</Text>
                <Stack direction="column" spacing={6}>
                  {hiringFeedData?.pages.flatMap((hiringFeedPage, pageIndex) => {
                    return hiringFeedPage.data.map((hiringFeedItem, placementIndex) => {
                      const hiringFeedWithDetails = transformResponseIntoHiringFeed(
                        hiringFeedItem,
                        hiringFeedPage.included,
                        credentialRequirementConfiguration
                      );

                      const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
                        {
                          id: hiringFeedWithDetails.id,
                          workplaceId: hiringFeedWithDetails.workplace.id,
                          workerTypes: hiringFeedWithDetails.workerTypes,
                          jobShifts: hiringFeedWithDetails.jobShifts,
                          transparencyPayRates: hiringFeedWithDetails.transparencyPayRates,
                          attributes: { payRange: hiringFeedWithDetails.payRange },
                        },
                        yearsOfExperienceByQualification,
                        false,
                        placementCandidate.payAndScheduleFormData?.benefits?.combined
                      );

                      return hiringFeedItem.meta.matchedType === "discovery" ? (
                        <Fragment key={hiringFeedItem.id}>
                          <PlacementCard
                            placement={hiringFeedToPlacement(hiringFeedWithDetails)}
                            placementCandidateId={placementCandidate.id}
                            isRestricted={placementCandidate.isRestricted ?? false}
                            interviewOnJobCardsConfig={interviewOnJobCardsConfig}
                            pageIndex={pageIndex}
                            placementIndex={placementIndex}
                            loadingPayData={isPayDataLoading}
                            minFlexRatingsCountToShow={minFlexRatingsCountToShow}
                            showWorkplaceRatings={showWorkplaceRatings}
                            minMaxPayEstimate={minMaxPayEstimate}
                            source="placement_card"
                            bookedInterviewDate={bookedPlacementIds.get(hiringFeedWithDetails.id)}
                            onOpenInterviewBooking={handleOpenInterviewBooking}
                          />
                        </Fragment>
                      ) : undefined;
                    });
                  })}
                </Stack>
              </Stack>
            </Box>
          ) : undefined}
        </>
      )}

      {activeHasNextPage && !placementCandidate.isRestricted && (
        <Stack direction="row" justifyContent="center">
          <Box sx={{ marginTop: 4, marginBottom: 10 }}>
            <InView
              as="div"
              onChange={async (inView) => {
                if (inView && !activeIsFetchingNextPage) {
                  await activeFetchNextPage();
                  logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
                    placementCandidateId: placementCandidate.id,
                    filters: { ...filter, distanceInMiles: filter?.distanceInMiles?.$lt },
                    action: "load_more_clicked",
                  });
                }
              }}
            />
            <Box sx={{ opacity: activeIsFetchingNextPage ? 1 : 0 }}>
              <LoadingSpinner size="medium" />
            </Box>
          </Box>
        </Stack>
      )}

      {isDefined(interviewBookingState) && (
        <CardInterviewBookingContainer
          placementId={interviewBookingState.placementId}
          workplaceId={interviewBookingState.workplaceId}
          placementCandidateId={interviewBookingState.placementCandidateId}
          placementCandidate={placementCandidate}
          preSelectedInterviewDate={interviewBookingState.preSelectedInterviewDate}
          modalState={interviewBookingModalState}
          onInterviewBooked={async (formattedDateTime) => {
            setBookedPlacementIds((previous) =>
              new Map(previous).set(interviewBookingState.placementId, formattedDateTime)
            );
            await activeRefetch();
          }}
        />
      )}
    </>
  );
}
